import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { dateFmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function CompanySettings() {
  const { data: c, isLoading } = useQuery({ queryKey: ["set-company"], queryFn: () => get("/settings/company") });
  if (isLoading || !c) return <Skeleton className="h-64" />;
  return (
    <div>
      <PageHeader title="Company Settings" subtitle="Organization-wide configuration." />
      <div className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-6">
        <div><Label>Company Name</Label><Input defaultValue={c.name} className="mt-1" data-testid="company-name" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Timezone</Label><Input defaultValue={c.timezone} className="mt-1" /></div>
          <div><Label>Fiscal Year Start</Label><Input defaultValue={c.fiscal_start} className="mt-1" /></div>
          <div><Label>Standard Daily Hours</Label><Input type="number" defaultValue={c.daily_hours} className="mt-1" /></div>
          <div><Label>Standard Monthly Hours</Label><Input type="number" defaultValue={c.monthly_hours} className="mt-1" /></div>
        </div>
        <div><Label>Work Week</Label><div className="mt-1 flex gap-1.5">{c.work_days.map((d) => <span key={d} className="rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">{d}</span>)}</div></div>
        <Button data-testid="save-company" onClick={() => toast.success("Company settings saved")}>Save Changes</Button>
      </div>
    </div>
  );
}

export function CurrencySettings() {
  const { data: c, isLoading } = useQuery({ queryKey: ["set-currency"], queryFn: () => get("/settings/currency") });
  if (isLoading || !c) return <Skeleton className="h-64" />;
  return (
    <div>
      <PageHeader title="Currency & Tax" subtitle="Base currency and tax rates." />
      <div className="max-w-xl space-y-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <Label>Base Currency</Label><div className="mt-1 font-display text-2xl font-bold">{c.base}</div>
          <Label className="mt-4 block">Enabled Currencies</Label>
          <div className="mt-1 flex gap-1.5">{c.enabled.map((cur) => <span key={cur} className="rounded-lg bg-muted px-2.5 py-1 text-xs font-medium">{cur}</span>)}</div>
        </div>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Tax Rate</th><th className="px-4 py-3 font-semibold">Rate</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
            <tbody>
              {c.tax_rates.map((t) => (
                <tr key={t.id} className="border-b border-border last:border-0"><td className="px-4 py-3 font-medium">{t.name}</td><td className="px-4 py-3">{t.rate}%</td><td className="px-4 py-3"><StatusBadge status={t.active ? "active" : "closed"} /></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function BackupSettings() {
  const { data: history, isLoading } = useQuery({ queryKey: ["set-backup"], queryFn: () => get("/settings/backup") });
  return (
    <div>
      <PageHeader title="Data Backup" subtitle="Automated backups run daily; retention 30 days.">
        <Button data-testid="backup-now" onClick={() => toast.success("Backup started")}>Backup Now</Button>
      </PageHeader>
      {isLoading ? <Skeleton className="h-64" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Size</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
            <tbody>
              {history?.map((b) => (
                <tr key={b.id} className={cn("border-b border-border last:border-0", b.status === "failed" && "bg-red-50 dark:bg-red-500/5")}>
                  <td className="px-4 py-3 font-medium">{b.type}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{dateFmt(b.date)}</td>
                  <td className="px-4 py-3">{b.size}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.status === "success" ? "approved" : "error"} label={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
